import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function useSimpleLogin() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [showForgotUsernameModal, setShowForgotUsernameModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showLoggedOut, setShowLoggedOut] = useState(false);

  const { login, markPasswordChanged, user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if user is already authenticated
  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) {
      return;
    }

    // Check if user is authenticated (either from context or localStorage)
    const token = localStorage.getItem('faithinsight_token');
    const savedUser = localStorage.getItem('faithinsight_user');
    const sessionExpired = localStorage.getItem('faithinsight_session_expired');

    // Don't redirect if session expired or logged out
    if (sessionExpired === 'true') {
      return;
    }

    // If we have a token and user data, or if auth context says we're authenticated
    if ((token && savedUser) || (isAuthenticated && user)) {
      // Get redirect URL from query parameters or localStorage
      const urlParams = new URLSearchParams(location.search);
      const redirectFromQuery = urlParams.get('redirect');
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');

      // Store redirect in localStorage if it's in the URL
      if (redirectFromQuery && !redirectFromStorage) {
        localStorage.setItem('faithinsight_login_redirect', redirectFromQuery);
      }

      const redirectUrl = redirectFromQuery || redirectFromStorage || '/dashboard';

      // Clear redirect from localStorage if it was used
      if (redirectFromStorage && redirectFromQuery) {
        localStorage.removeItem('faithinsight_login_redirect');
      }

      // Navigate to the redirect URL
      if (redirectUrl === '/catalog') {
        window.location.href = '/portal/catalog?redirect=/catalog';
      } else {
        navigate(redirectUrl, { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, user, location.search, navigate]);

  // Check for session expired or logged out flags on component mount
  useEffect(() => {
    const loggedOut = localStorage.getItem('faithinsight_logged_out');
    const sessionExpired = localStorage.getItem('faithinsight_session_expired');

    if (loggedOut === 'true') {
      setShowLoggedOut(true);
      // Clear the flag since we're showing the message
      localStorage.removeItem('faithinsight_logged_out');
    } else if (sessionExpired === 'true') {
      setShowSessionExpired(true);
      // Clear the flag since we're showing the message
      localStorage.removeItem('faithinsight_session_expired');
    }
  }, []);

  // Preserve redirect parameter in localStorage when component mounts or URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');
    const redirectFromState = location.state?.from?.pathname;

    // Store redirect in localStorage so it persists through errors and password reset flows
    const redirectToStore = redirectFromQuery || redirectFromState;
    if (redirectToStore) {
      localStorage.setItem('faithinsight_login_redirect', redirectToStore);
    }
  }, [location.search, location.state]);

  const getRedirectUrl = (clearAfterUse = false) => {
    // Check for redirect in query parameters first
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');

    // Then check for redirect in location state
    const redirectFromState = location.state?.from?.pathname;

    // Then check localStorage (preserved from previous visits)
    const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');

    // Prioritize query parameter, then state, then localStorage, then default to dashboard
    const redirectUrl = redirectFromQuery || redirectFromState || redirectFromStorage || '/dashboard';

    // Only clear from localStorage after successful login (when clearAfterUse is true)
    if (clearAfterUse && redirectFromStorage) {
      localStorage.removeItem('faithinsight_login_redirect');
    }

    return redirectUrl;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Clear logged out and session expired messages when user attempts to log in
    setShowLoggedOut(false);
    setShowSessionExpired(false);

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    if (!formData.password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    const result = await login(formData.email, formData.password);

    if (result.success) {
      if (result.requiresPasswordChange) {
        setShowPasswordChangeModal(true);
      } else {
        // Get redirect URL immediately
        const redirectUrl = getRedirectUrl(false);

        // Navigate immediately before any re-renders can happen
        // Use window.location for catalog to ensure immediate navigation without showing dashboard
        if (redirectUrl === '/catalog') {
          // Navigate to catalog with redirect parameter (full page reload to prevent dashboard flash)
          window.location.href = '/portal/catalog?redirect=/catalog';
        } else {
          // Clear redirect from localStorage after successful login for non-catalog redirects
          const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
          if (redirectFromStorage) {
            localStorage.removeItem('faithinsight_login_redirect');
          }
          navigate(redirectUrl, { replace: true });
        }
      }
    } else {
      setError(result.error || 'Login failed. Please try again.');
      // Don't clear redirect on error - keep it for next login attempt
    }

    setLoading(false);
  };

  const handlePasswordChangeSuccess = () => {
    markPasswordChanged();
    setShowPasswordChangeModal(false);
    // Get redirect URL immediately
    const redirectUrl = getRedirectUrl(false);

    // Navigate immediately before any re-renders can happen
    // Use window.location for catalog to ensure immediate navigation
    if (redirectUrl === '/catalog') {
      // Navigate to catalog with redirect parameter
      window.location.href = '/portal/catalog?redirect=/catalog';
    } else {
      // Clear redirect from localStorage after successful login for non-catalog redirects
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
      if (redirectFromStorage) {
        localStorage.removeItem('faithinsight_login_redirect');
      }
      navigate(redirectUrl, { replace: true });
    }
  };

  const handleNeedHelp = () => {
    // Open email client to admin
    window.open('mailto:admin@faithinsight.org');
  };

  return {
    formData,
    showPassword,
    setShowPassword,
    loading,
    error,
    showPasswordChangeModal,
    setShowPasswordChangeModal,
    showForgotUsernameModal,
    setShowForgotUsernameModal,
    showForgotPasswordModal,
    setShowForgotPasswordModal,
    showSessionExpired,
    showLoggedOut,
    location,
    navigate,
    handleChange,
    handleSubmit,
    handlePasswordChangeSuccess,
    handleNeedHelp
  };
}
